import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Form,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

//console.log("Stripe key:", process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

console.log("PROCESSING: ", stripePromise);
const cardElementOptions = {
  style: {
    base: {
      color: "var(--text)",
      fontFamily: "Arial, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "var(--text-secondary)",
      },
    },
    invalid: {
      color: "#dc3545",
      iconColor: "#dc3545",
    },
  },
};

const StripeInput = ({ component: Component, onChange }) => (
  <div className="stripe-input-container">
    <Component options={cardElementOptions} onChange={onChange} />
  </div>
);

// Add CSS for validation styling
const validationStyles = `
  .stripe-input-container {
    border: 2px solid #e1e5e9;
    border-radius: 12px;
    padding: 0;
    background: #ffffff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    animation: fadeInUp 0.6s ease-out;
  }
  
  .stripe-input-container .StripeElement {
    border: none !important;
    padding: 16px 20px;
    font-size: 16px;
    font-weight: 500;
    color: #000000;
    background: transparent;
  }
  
  .stripe-input-container:hover {
    border-color: var(--primary-500);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
    transform: translateY(-1px);
  }
  
  .stripe-input-container:focus-within {
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    transform: translateY(-2px);
  }
  
  .stripe-input-container.is-invalid {
    border: 2px solid #dc3545;
    border-radius: 12px;
    padding: 0;
    animation: shake 0.5s ease-in-out;
  }
  
  .stripe-input-container.is-invalid .StripeElement {
    border: none !important;
    padding: 16px 20px;
  }
  
  .invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.5rem;
    font-size: 0.875em;
    color: #dc3545;
    font-weight: 500;
    animation: fadeIn 0.3s ease-in;
  }
  
  .form-control:focus {
    border-color: var(--primary-500) !important;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1) !important;
    transform: translateY(-1px);
  }
  
  .form-control:hover {
    border-color: var(--primary-500) !important;
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
    transform: translateY(-1px);
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(40px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FUNCTION_URL =
  "https://us-central1-epcspark-3ce7a.cloudfunctions.net/createPaymentIntent";

const TRIAL_FUNCTION_URL =
  "https://us-central1-epcspark-3ce7a.cloudfunctions.net/createTrialSubscription";

async function createPaymentIntent(amount, currency = "gbp", retries = 3) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Creating payment intent (attempt ${attempt}/${retries}):`, {
        amount,
        currency,
      });

      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, currency }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!data.clientSecret) {
        throw new Error("No client secret received from server");
      }

      console.log("Payment intent created successfully:", data.paymentIntentId);
      return data.clientSecret;
    } catch (error) {
      console.error(
        `Payment intent creation attempt ${attempt} failed:`,
        error
      );

      if (attempt === retries) {
        throw new Error(
          `Failed to create payment intent after ${retries} attempts: ${error.message}`
        );
      }

      // Wait before retrying (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, attempt) * 1000)
      );
    }
  }
}

async function createTrialSubscription(user, plan, isYearly, retries = 3) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Creating trial subscription (attempt ${attempt}/${retries}):`, {
        customerEmail: user.email,
        customerName: `${user.firstName} ${user.lastName}`,
        planName: plan.name,
        isYearly,
      });

      const response = await fetch(TRIAL_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerEmail: user.email,
          customerName: `${user.firstName} ${user.lastName}`,
          planName: plan.name,
          isYearly,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error("Failed to create trial subscription");
      }

      console.log("Trial subscription created successfully:", data.subscription.id);
      return data.subscription;
    } catch (error) {
      console.error(
        `Trial subscription creation attempt ${attempt} failed:`,
        error
      );

      if (attempt === retries) {
        throw new Error(
          `Failed to create trial subscription after ${retries} attempts: ${error.message}`
        );
      }

      // Wait before retrying (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, attempt) * 1000)
      );
    }
  }
}

const CheckoutForm = ({ user, plan, isYearly }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardholderName, setCardholderName] = useState(
    `${user.firstName} ${user.lastName}`
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [cardState, setCardState] = useState({
    numberError: null,
    expiryError: null,
    cvcError: null,
    numberComplete: false,
    expiryComplete: false,
    cvcComplete: false,
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Check if this is a trial subscription (Standard plan only)
  const isTrial = plan.name.toLowerCase() === 'standard';
  const amount = isTrial ? 0 : (isYearly ? plan.price.yearly * 100 : plan.price.monthly * 100);
  const displayAmount = isTrial ? 0 : (isYearly ? plan.price.yearly : plan.price.monthly);

  // Remove all custom validation for card fields
  const validateForm = () => {
    const errors = {};

    // Validate cardholder name
    if (!cardholderName || cardholderName.trim() === "") {
      errors.cardholderName = "Please enter the name as shown on your card.";
    } else if (cardholderName.trim().length < 2) {
      errors.cardholderName =
        "Please enter a valid name (at least 2 characters).";
    }

    // Validate card number
    if (!cardState.numberComplete) {
      errors.cardNumber = "Please enter your card number.";
    }

    // Validate expiry date
    if (!cardState.expiryComplete) {
      errors.expiryDate = "Please enter the expiry date.";
    }

    // Validate CVC
    if (!cardState.cvcComplete) {
      errors.cvc = "Please enter the CVC.";
    }

    return errors;
  };

  // Stripe onChange handlers
  const handleCardNumberChange = (event) => {
    setCardState((prev) => ({
      ...prev,
      numberError: event.error ? event.error.message : null,
      numberComplete: event.complete,
    }));
    if (validationErrors.cardNumber) {
      setValidationErrors((prev) => ({ ...prev, cardNumber: null }));
    }
  };

  const handleExpiryDateChange = (event) => {
    setCardState((prev) => ({
      ...prev,
      expiryError: event.error ? event.error.message : null,
      expiryComplete: event.complete,
    }));
    if (validationErrors.expiryDate) {
      setValidationErrors((prev) => ({ ...prev, expiryDate: null }));
    }
  };

  const handleCvcChange = (event) => {
    setCardState((prev) => ({
      ...prev,
      cvcError: event.error ? event.error.message : null,
      cvcComplete: event.complete,
    }));
    if (validationErrors.cvc) {
      setValidationErrors((prev) => ({ ...prev, cvc: null }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setValidationErrors({});

    // Only validate cardholder name
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setLoading(false);
      return;
    }

    if (!stripe || !elements) {
      setError("Stripe.js has not loaded yet.");
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setError("Card details not found.");
      setLoading(false);
      return;
    }

    try {
      if (isTrial) {
        // For trial subscriptions, create a proper Stripe trial subscription
        console.log("Creating trial subscription for Standard plan");
        const trialSubscription = await createTrialSubscription(user, plan, isYearly);
        
        if (trialSubscription) {
          console.log("Trial subscription created successfully:", trialSubscription);
          setLoading(false);
          navigate("/success", { 
            state: { 
              plan, 
              isYearly, 
              isTrial: true,
              stripeSubscription: trialSubscription 
            } 
          });
        } else {
          throw new Error("Failed to create trial subscription");
        }
      } else {
        // Create a fresh payment intent for paid subscriptions
        const clientSecret = await createPaymentIntent(amount, "gbp");

        if (!clientSecret) {
          throw new Error("Failed to create payment intent");
        }

        const { error: paymentError, paymentIntent } =
          await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
                email: user.email,
              },
            },
          });

        if (paymentError) {
          console.error("Payment error:", paymentError);
          setError(paymentError.message || "Payment failed. Please try again.");
          setLoading(false);
          return;
        }

        if (paymentIntent && paymentIntent.status === "succeeded") {
          console.log("Payment successful:", paymentIntent);
          setLoading(false);
          navigate("/success", { state: { plan, isYearly, isTrial: false } });
        } else {
          console.error("Payment intent status:", paymentIntent?.status);
          setError("Payment was not completed successfully. Please try again.");
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Payment submission error:", error);
      setError(error.message || "Payment failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit} className="signup-form-wrapper" style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "20px",
        padding: "40px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        maxWidth: "500px",
        margin: "0 auto",
        animation: "slideInUp 0.8s ease-out"
      }}>
        <div
          style={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: "32px",
            paddingBottom: "24px",
            borderBottom: "1px solid var(--border)"
          }}
        >
          <button
            type="button"
            className="btn btn-back-glow me-3"
            style={{
              minWidth: 100,
              fontWeight: "600",
              fontSize: "14px",
              borderRadius: "12px",
              background: "var(--surface-2)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              padding: "10px 16px",
              boxShadow: "0 2px 8px rgba(139, 92, 246, 0.08)",
              transition: "all 0.2s ease"
            }}
            onClick={() => navigate("/subscription")}
          >
            ← Back
          </button>
          <div style={{ flex: 1 }} />
          <div
            style={{
              fontWeight: "700",
              fontSize: "16px",
              background: "linear-gradient(135deg, var(--primary-500) 0%, var(--cyan-400) 100%)",
              color: "white",
              padding: "12px 20px",
              borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(139, 92, 246, 0.2)"
            }}
          >
            {isTrial ? "FREE TRIAL" : `£${displayAmount.toFixed(2)} GBP`}
          </div>
        </div>
        
        {isTrial && (
          <Alert variant="info" className="mb-3" style={{
            background: "rgba(139, 92, 246, 0.1)",
            border: "1px solid rgba(139, 92, 246, 0.3)",
            color: "var(--text)"
          }}>
            <strong style={{ color: "var(--text)" }}>30-Day Free Trial</strong><br />
            <span style={{ color: "var(--text-secondary)" }}>
              Start your free trial today. No charges will be made during the trial period. 
              You can cancel anytime before the trial ends.
            </span>
          </Alert>
        )}

        <Form.Group className="mb-4">
          <Form.Label style={{ 
            color: "var(--text)", 
            fontWeight: "700", 
            fontSize: "14px",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
            Enter name as appears on card
          </Form.Label>
          <Form.Control
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            required
            placeholder="Enter name as shown on card"
            isInvalid={!!validationErrors.cardholderName}
            style={{
              border: "2px solid #e1e5e9",
              borderRadius: "12px",
              padding: "16px 20px",
              backgroundColor: "#ffffff",
              color: "#000000",
              transition: "all 0.3s ease",
              fontSize: "16px",
              fontWeight: "500",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
            }}
          />
          {validationErrors.cardholderName && (
            <Form.Control.Feedback type="invalid">
              {validationErrors.cardholderName}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label style={{ 
            color: "var(--text)", 
            fontWeight: "700", 
            fontSize: "14px",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>Card Number</Form.Label>
          <div
            className={
              cardState.numberError
                ? "stripe-input-container is-invalid"
                : "stripe-input-container"
            }
          >
            <StripeInput
              component={CardNumberElement}
              onChange={handleCardNumberChange}
            />
          </div>
          {cardState.numberError && (
            <div className="invalid-feedback d-block">
              {cardState.numberError}
            </div>
          )}
          {validationErrors.cardNumber && (
            <div className="invalid-feedback d-block">
              {validationErrors.cardNumber}
            </div>
          )}
        </Form.Group>

        <Row>
          <Col>
            <Form.Group className="mb-4">
              <Form.Label style={{ 
                color: "var(--text)", 
                fontWeight: "700", 
                fontSize: "14px",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>Expiry Date</Form.Label>
              <div
                className={
                  cardState.expiryError
                    ? "stripe-input-container is-invalid"
                    : "stripe-input-container"
                }
              >
                <StripeInput
                  component={CardExpiryElement}
                  onChange={handleExpiryDateChange}
                />
              </div>
              {cardState.expiryError && (
                <div className="invalid-feedback d-block">
                  {cardState.expiryError}
                </div>
              )}
              {validationErrors.expiryDate && (
                <div className="invalid-feedback d-block">
                  {validationErrors.expiryDate}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-4">
              <Form.Label style={{ 
                color: "var(--text)", 
                fontWeight: "700", 
                fontSize: "14px",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>CVC</Form.Label>
              <div
                className={
                  cardState.cvcError
                    ? "stripe-input-container is-invalid"
                    : "stripe-input-container"
                }
              >
                <StripeInput
                  component={CardCvcElement}
                  onChange={handleCvcChange}
                />
              </div>
              {cardState.cvcError && (
                <div className="invalid-feedback d-block">
                  {cardState.cvcError}
                </div>
              )}
              {validationErrors.cvc && (
                <div className="invalid-feedback d-block">
                  {validationErrors.cvc}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="termsCheckbox" className="mb-4">
          <Form.Check
            type="checkbox"
            label={
              <span style={{
                color: 'var(--text)',
                fontSize: '14px',
                fontWeight: '500',
                lineHeight: '1.5'
              }}>
                I agree to the{" "}
                <a
                  href="/payment-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--primary-500)',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Payment Terms and Conditions
                </a>
              </span>
            }
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            required
          />
        </Form.Group>

        <Button 
          type="submit" 
          className="w-100 btn-aiwave-primary" 
          disabled={loading || !agreedToTerms}
          style={{
            background: "linear-gradient(135deg, var(--primary-500) 0%, var(--cyan-400) 100%)",
            border: "none",
            color: "white",
            fontWeight: "700",
            fontSize: "16px",
            padding: "16px 24px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(139, 92, 246, 0.3)",
            transition: "all 0.3s ease",
            marginBottom: "24px"
          }}
        >
          {loading ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : isTrial ? (
            "Start Free Trial"
          ) : (
            `Pay & Subscribe`
          )}
        </Button>
        {error && (
          <Alert variant="danger" className="mt-3" style={{
            background: "rgba(220, 53, 69, 0.1)",
            border: "1px solid rgba(220, 53, 69, 0.3)",
            color: "#dc3545"
          }}>
            {error}
          </Alert>
        )}
      </Form>
      <div
        style={{
          fontSize: "13px",
          color: "var(--text-secondary)",
          marginTop: "24px",
          textAlign: "center",
          padding: "16px",
          background: "var(--surface-2)",
          borderRadius: "12px",
          border: "1px solid var(--border)"
        }}
      >
        <span role="img" aria-label="lock" style={{ fontSize: "16px", marginRight: "8px" }}>
          🔒
        </span>
        Your payment is securely processed by Stripe. We do not store your card details.
        <br />
        <span style={{ fontWeight: "600", marginTop: "4px", display: "inline-block" }}>
          Accepted cards: Visa, MasterCard, American Express
        </span>
      </div>
      <style>{validationStyles}</style>
    </>
  );
};

export default function Payment() {
  const location = useLocation();
  const navigate = useNavigate();

  const { user, plan, isYearly } = location.state || {};

  if (!user || !plan) {
    // Redirect to pricing if essential data is missing
    navigate("/#pricing");
    return null;
  }

  // const price = isYearly ? plan.price.yearly : plan.price.monthly;
  // const period = isYearly ? "year" : "month";

  return (
    <div className="signup-container">
      <Container>
        <div className="text-center mb-5">
          <h1 className="signup-heading" style={{ color: "var(--text)" }}>Complete Your Subscription</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "16px", marginBottom: "0" }}>
            Securely finalize your payment for the {plan.name} plan.
          </p>
        </div>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {plan && (
              <p style={{ 
                color: "var(--text-secondary)", 
                textAlign: "center", 
                marginBottom: "24px",
                fontSize: "14px"
              }}>
                You selected the <strong style={{ color: "var(--text)" }}>{plan.name}</strong> plan –{" "}
                {isYearly ? "Yearly" : "Monthly"} | Includes {plan.assessments}
              </p>
            )}
            <Elements stripe={stripePromise}>
              <CheckoutForm user={user} plan={plan} isYearly={isYearly} />
            </Elements>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
